export interface ShortcutConfig {
    handler: Function;
    disabled?: boolean | (() => boolean); // Reactive disabled flag
    disabledByInputs?: boolean; // If this flag is true, the shortcut will be disabled when an input or textarea is focused
}

export interface ShortcutsConfig {
    [key: string]: ShortcutConfig | Function;
}

interface Shortcut {
    handler: Function;
    isEnabled: () => boolean;
    chained: boolean;
    key: string;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
}

const keyMap: Record<string, { mac: string; win: string }> = {
    meta: { mac: "⌘", win: "Ctrl" },
    ctrl: { mac: "⌃", win: "Ctrl" },
    alt: { mac: "⌥", win: "Alt" },
    shift: { mac: "⇧", win: "⇧" },
    backspace: { mac: "⌫", win: "⌫" },
    escape: { mac: "⎋", win: "Esc" },
    delete: { mac: "⌦", win: "⌦" },
    enter: { mac: "↵", win: "↵" },
    tab: { mac: "⇥", win: "⇥" },
    arrowup: { mac: "↑", win: "↑" },
    arrowright: { mac: "→", win: "→" },
    arrowdown: { mac: "↓", win: "↓" },
    arrowleft: { mac: "←", win: "←" },
    pageup: { mac: "⇞", win: "⇞" },
    pagedown: { mac: "⇟", win: "⇟" },
    home: { mac: "↖", win: "Pos1" },
    end: { mac: "↘", win: "End" },
}

const detectMac = () => typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent)

export function isMobileDevice() {
    if (typeof navigator === 'undefined') return false
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export function useKeymap(shortcut: string) {
    if (!useFeature('shortcuts') || isMobileDevice()) return "" 
    const platform = computed(() => (detectMac() ? "mac" : "win"))
    return shortcut
        .split("_")
        .map((part) => keyMap[part]?.[platform.value] || part.toUpperCase())
        .join(platform.value === "mac" ? "" : "+")
}

function inputIsFocused() {
    const el = useActiveElement().value
    return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.contentEditable === "true")
}

function evaluateDisabled(val: any): boolean {
    if (typeof val === "function") return !!val()
    if (val?.value !== undefined) return !!val.value
    return !!val
}

function parseShortcut(key: string, cfg: ShortcutConfig | Function, macOS: boolean): Shortcut | null {
    if (!cfg) return null
    const isChained = key.includes("-")
    const keyLower = key.toLowerCase()
    let shortcut: Partial<Shortcut>

    if (isChained) {
        shortcut = {
            key: keyLower,
            metaKey: false,
            ctrlKey: false,
            shiftKey: false,
            altKey: false,
        }
    } else {
        const parts = keyLower.split("_")
        shortcut = {
            key: parts.filter((p) => !["meta", "ctrl", "shift", "alt"].includes(p)).join("_"),
            metaKey: parts.includes("meta"),
            ctrlKey: parts.includes("ctrl"),
            shiftKey: parts.includes("shift"),
            altKey: parts.includes("alt"),
        }
        if (!macOS && shortcut.metaKey && !shortcut.ctrlKey) {
            shortcut.metaKey = false
            shortcut.ctrlKey = true
        }
    }

    shortcut.chained = isChained

    const shortcutCfg = typeof cfg === "function" ? { handler: cfg } : cfg
    if (!shortcutCfg.handler) return null

    shortcut.handler = shortcutCfg.handler
    shortcut.isEnabled = () => {
        if (evaluateDisabled(shortcutCfg.disabled)) return false
        if (shortcutCfg.disabledByInputs && inputIsFocused()) return false
        return true
    }

    return shortcut as Shortcut
}

export function useShortcuts(config?: ShortcutsConfig) {   
    if (!useFeature('shortcuts') || isMobileDevice()) {
        // Do not register shortcuts on mobile devices
        return
    }
    const macOS = computed(() => detectMac())
    const chainedInputs = ref<string[]>([])
    let shortcuts: Shortcut[] = []

    const clearChainedInputs = () => (chainedInputs.value = [])
    const debouncedClearChainedInputs = useDebounceFn(clearChainedInputs, 800)

    function handleKeyDown(e: KeyboardEvent) {
        if (!e.key) return
        
        if (document.activeElement instanceof HTMLElement && document.activeElement.querySelector('button')) {
            if (!inputIsFocused() && document.activeElement.tagName !== 'BODY') {
                document.activeElement.blur()
            }
        }

        chainedInputs.value.push(e.key)

        // Chained shortcuts (eg. "g-t")
        if (chainedInputs.value.length >= 2) {
            const seqKey = chainedInputs.value.slice(-2).join("-")
            for (const s of shortcuts.filter((s) => s.chained)) {
                if (s.key === seqKey && s.isEnabled()) {
                    e.preventDefault()
                    s.handler()
                    clearChainedInputs()
                    return
                }
            }
        }

        // Combo shortcuts ("meta_s", "ctrl_k", etc)
        for (const s of shortcuts.filter((s) => !s.chained)) {
            let keyMatch = false
            if (e.altKey && s.altKey) {
                // Use physical key code instead of character value for Alt combinations
                const keyCode = e.code
                let actualKey = null
                
                if (keyCode) {
                    if (keyCode.startsWith("Key")) {
                        actualKey = keyCode.substring(3).toLowerCase()
                    } else if (keyCode === "Period") {
                        actualKey = "."
                    } else if (keyCode === "Comma") {
                        actualKey = ","
                    } else if (keyCode === "Semicolon") {
                        actualKey = ";"
                    } else if (keyCode === "Quote") {
                        actualKey = "'"
                    } else if (keyCode === "BracketLeft") {
                        actualKey = "["
                    } else if (keyCode === "Minus") {
                        actualKey = "-"
                    } else if (keyCode === "Equal") {
                        actualKey = "="
                    } else if (keyCode === "Backslash") {
                        actualKey = "\\"
                    } else if (keyCode === "Slash") {
                        actualKey = "/"
                    } else if (keyCode.startsWith("Digit")) {
                        actualKey = keyCode.substring(5)
                    }
                }
                
                keyMatch = actualKey === s.key
            } else {
                keyMatch = e.key.toLowerCase() === s.key
            }
            
            const modMatch =
                e.metaKey === s.metaKey &&
                e.ctrlKey === s.ctrlKey &&
                e.shiftKey === s.shiftKey &&
                e.altKey === s.altKey

            if (keyMatch && modMatch && s.isEnabled()) {
                e.preventDefault()
                s.handler()
                clearChainedInputs()
                return
            }
        }

        debouncedClearChainedInputs()
    }

    if (config) {
        shortcuts = Object.entries(config)
            .map(([key, cfg]) => parseShortcut(key, cfg, macOS.value))
            .filter(Boolean) as Shortcut[]

        useEventListener("keydown", handleKeyDown)
    }
}
